/**
 * Splits an Ask answer into plain runs and quoted runs, so the panel can offer
 * the quoted ones as jumps into the transcript (PLAT-11806 follow-up).
 *
 * Only double quotes open a run — curly “…”, which is what the model writes,
 * and straight "…". Apostrophes are deliberately left out: ’ and ' open
 * nothing, so "the speaker’s point" can't start a run that swallows the rest
 * of the paragraph.
 */

export interface AnswerPart {
  /** The run exactly as written, quote marks and all. */
  text: string;
  /** The quoted words alone, when the run is a quotation worth locating. */
  quote?: string;
}

/**
 * Under three words a quotation is too common to place in a transcript with
 * any confidence — "the needle" occurs wherever it likes, and a link that
 * lands somewhere arbitrary is worse than no link.
 */
const MIN_QUOTE_WORDS = 3;

const QUOTED_RUN = /[“"]([^“”"]+)[”"]/g;

export function splitAnswerQuotes(text: string): AnswerPart[] {
  const parts: AnswerPart[] = [];
  let consumed = 0;

  for (const match of text.matchAll(QUOTED_RUN)) {
    const start = match.index ?? 0;
    if (start > consumed) parts.push({ text: text.slice(consumed, start) });

    const inner = match[1];
    const wordCount = inner.trim().split(/\s+/).filter(Boolean).length;
    parts.push(
      wordCount >= MIN_QUOTE_WORDS
        ? { text: match[0], quote: inner }
        : { text: match[0] },
    );
    consumed = start + match[0].length;
  }

  if (consumed < text.length) parts.push({ text: text.slice(consumed) });
  return parts;
}
